import React from 'react'
import Modal from '../../../components/Modal'
import DateRangeFilter from '../../../components/DateRangeFilter'
import AutoSalaryBreakdown from '../../../components/AutoSalaryBreakdown'
import { firstDayOfMonth, lastDayOfMonth, formatMoney, formatDate } from '../utils/paymentHelpers'
import * as api from '../../../lib/api'

export default function PaySalaryModal({
  payModalData,
  isFromAnalytics = false,
  setPayModalData,
  customPeriodStart,
  setCustomPeriodStart,
  customPeriodEnd,
  setCustomPeriodEnd,
  employeeShifts,
  isCalculating,
  includeRate,
  setIncludeRate,
  includeBonuses,
  setIncludeBonuses,
  includeAdjustments,
  setIncludeAdjustments,
  isManualAmount,
  setIsManualAmount,
  calculatedData,
  payAmount,
  setPayAmount,
  payComment,
  setPayComment,
  setIsCommentManuallyEdited,
  showPayModalDetails,
  setShowPayModalDetails,
  profile,
  isAdmin,
  filterPeriodStart,
  filterPeriodEnd,
  setSalaryDetailModal,
  payingSalary,
  setPayingSalary,
  setRefreshTrigger,
  toast
}) {
  return (
    <Modal
      isOpen={!!payModalData}
      onClose={() => setPayModalData(null)}
      title="Проведение выплаты ЗП"
    >
      {payModalData && (
        <div className="pay-salary-modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--surface-hover)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '24px', display:'flex', alignItems:'center', justifyContent:'center' }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--wb-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Сотрудник</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{payModalData.employeeName}</span>
                {payModalData.employment_type === 'substitute' && (
                  <span style={{ fontSize: '11px', color: '#d97706', background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.25)', padding: '2px 8px', borderRadius: '6px', fontWeight: 600 }}>
                    Подменный · еженедельно
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Расчётный период сотрудника:</label>
            <DateRangeFilter
              startDate={customPeriodStart}
              endDate={customPeriodEnd}
              onApply={(s, e) => {
                setCustomPeriodStart(s || '')
                setCustomPeriodEnd(e || '')
              }}
              onReset={() => {
                const start = payModalData.naturalPeriod?.start || payModalData.period?.start || filterPeriodStart || firstDayOfMonth()
                const end = payModalData.naturalPeriod?.end || payModalData.period?.end || filterPeriodEnd || lastDayOfMonth()
                setCustomPeriodStart(start)
                setCustomPeriodEnd(end)
              }}
              shiftMarkers={employeeShifts}
              quickPresets={(() => {
                const now = new Date()
                const m = now.getMonth() + 1
                const y = now.getFullYear()
                const config = calculatedData?.config || {}

                if (config.payoutFrequency === 'shifts') {
                  const shiftsCount = parseInt(config.payoutShiftsCount) || 10
                  const sortedShifts = [...(employeeShifts || [])]
                    .filter(s => {
                      let hours = parseFloat(s.hours) || 0
                      if (s.approval_status === 'pending' || s.approval_status === 'rejected') {
                        hours = 0
                      }
                      return hours > 0 || s.started_at
                    })
                    .sort((a, b) => new Date(a.work_date) - new Date(b.work_date))

                  const shiftPeriods = []
                  for (let i = 0; i < sortedShifts.length; i += shiftsCount) {
                    const chunk = sortedShifts.slice(i, i + shiftsCount)
                    const firstDate = chunk[0].work_date.split('T')[0]
                    const lastDate = chunk[chunk.length - 1].work_date.split('T')[0]
                    shiftPeriods.push({
                      start: firstDate,
                      end: lastDate,
                      label: `Смены ${i + 1}–${i + chunk.length}`
                    })
                  }
                  return [
                    ...shiftPeriods.map(p => ({ label: p.label, getValue: () => [p.start, p.end] })),
                    { label: 'Этот месяц', getValue: () => [firstDayOfMonth(), lastDayOfMonth()] },
                  ]
                }

                const effectiveFreq = api.getEffectivePayoutFrequency ? api.getEffectivePayoutFrequency(payModalData, config) : (payModalData?.employment_type === 'substitute' ? 'weekly' : (config.payoutFrequency || 'bi_monthly'))
                const cutover = config?.settlementCutoverDate || config?.settlement_cutover_date || null
                const periods = api.getSalaryPeriods(payModalData?.hired_at || null, y, m, effectiveFreq, payModalData?.pay_day, [], cutover)
                return [
                  ...periods.map(p => ({ label: `${p.label}`, getValue: () => [p.start, p.end] })),
                  { label: 'Этот месяц', getValue: () => [firstDayOfMonth(), lastDayOfMonth()] },
                ]
              })()}
            />
            {Boolean(payModalData?.isFromAnalytics || isFromAnalytics) && (
              <div
                className="analytics-pay-period-hint"
                style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.4',
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.7 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                <span>Выплата рассчитывается по графику сотрудника, независимо от выбранного периода просмотра.</span>
              </div>
            )}
          </div>

          <div style={{ background: 'var(--surface-color)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '14px', position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', borderBottom: '1px solid var(--border-color)', paddingBottom: '6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Составляющие выплаты за период:</span>
              {isCalculating && (
                <span style={{ fontSize: '11px', color: 'var(--wb-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span className="spinner-mini" style={{ width: '10px', height: '10px', border: '2px solid var(--wb-primary)', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }}></span>
                  пересчёт...
                </span>
              )}
            </div>

            {(() => {
              const defaultStart = payModalData?.naturalPeriod?.start || payModalData?.period?.start
              const defaultEnd = payModalData?.naturalPeriod?.end || payModalData?.period?.end
              const isDefaultPeriod = !payModalData?.isFromAnalytics && customPeriodStart === defaultStart && customPeriodEnd === defaultEnd

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '6px 8px', borderRadius: '8px', background: includeRate ? 'var(--surface-hover)' : 'transparent', transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={includeRate}
                        onChange={(e) => setIncludeRate(e.target.checked)}
                        disabled={isManualAmount}
                        style={{ width: '16px', height: '16px', accentColor: 'var(--wb-primary)', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', fontWeight: 500, color: includeRate ? 'var(--text-primary)' : 'var(--text-secondary)' }}>Фикс (оклад / ставка)</span>
                    </div>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: includeRate ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                      {isDefaultPeriod ? formatMoney(payModalData.rateDebt || 0) : (calculatedData ? formatMoney(calculatedData.rateDebt || 0) : '...')}
                    </span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: payModalData?.bonusesPayable === false ? 'not-allowed' : 'pointer', padding: '6px 8px', borderRadius: '8px', background: includeBonuses ? 'var(--surface-hover)' : 'transparent', transition: 'all 0.2s', opacity: payModalData?.bonusesPayable === false ? 0.5 : 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={includeBonuses}
                        onChange={(e) => setIncludeBonuses(e.target.checked)}
                        disabled={isManualAmount || payModalData?.bonusesPayable === false}
                        style={{ width: '16px', height: '16px', accentColor: 'var(--wb-primary)', cursor: payModalData?.bonusesPayable === false ? 'not-allowed' : 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', fontWeight: 500, color: includeBonuses ? 'var(--text-primary)' : 'var(--text-secondary)' }}>Бонусы (за операции)</span>
                    </div>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: includeBonuses ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                      {isDefaultPeriod ? formatMoney(payModalData.bonusesDebt || 0) : (calculatedData ? formatMoney(calculatedData.bonusesDebt || 0) : '...')}
                    </span>
                  </label>
                  {payModalData?.bonusesPayable === false && (
                    <div style={{
                      fontSize: '11px', color: 'var(--warning, #F59E0B)',
                      padding: '4px 8px 2px 32px',
                      display: 'flex', alignItems: 'center', gap: '4px'
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      Раздельная выплата — бонусы{payModalData.bonusPayoutDateStr ? ` доступны с ${payModalData.bonusPayoutDateStr}` : ' будут доступны позже'}
                    </div>
                  )}
                </div>
              )
            })()}
          </div>

          {(() => {
            const isDataUnavailable = Boolean(
              payModalData?.financialDataUnavailable ||
              calculatedData?.financialDataUnavailable ||
              payModalData?.status === 'data_unavailable' ||
              calculatedData?.status === 'data_unavailable'
            )
            if (isDataUnavailable) {
              return (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid rgba(239, 68, 68, 0.25)',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  fontSize: '13px',
                  color: '#b91c1c',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span>Данные для расчёта недоступны</span>
                  </div>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                    {payModalData?.unresolvedLegacyNotice || calculatedData?.unresolvedLegacyNotice || 'Не удалось загрузить все данные для расчёта зарплаты. Выплата временно заблокирована — обновите данные и повторите попытку.'}
                  </span>
                </div>
              )
            }

            if (calculatedData?.hasUnresolvedLegacy || payModalData?.hasUnresolvedLegacy || calculatedData?.isReviewRequired || payModalData?.isReviewRequired) {
              return (
                <div style={{
                  background: 'rgba(245, 158, 11, 0.12)',
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  fontSize: '13px',
                  color: '#b45309',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span>Требуется сверка исторических выплат</span>
                  </div>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                    За данный или пересекающийся период обнаружены исторические выплаты без точной разбивки по расчётным периодам. Автоматическая выплата заблокирована во избежание повторной выплаты уже выплаченных сумм.
                  </span>
                </div>
              )
            }

            return null
          })()}

          {calculatedData && !isCalculating && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.06), rgba(139, 92, 246, 0.04))',
              border: '1px solid rgba(99, 102, 241, 0.15)',
              borderRadius: '12px', padding: '12px 14px'
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 20px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--wb-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  <span><strong style={{ color: 'var(--text-primary)' }}>{calculatedData.daysWorked || 0}</strong> смен</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--wb-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span><strong style={{ color: 'var(--text-primary)' }}>{Number(calculatedData.totalHours || 0).toFixed(1).replace(/\.0$/, '')}</strong> ч</span>
                </div>
                {(calculatedData.daysWorked || 0) > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--wb-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/></svg>
                    <span>Ø <strong style={{ color: 'var(--text-primary)' }}>{formatMoney(Math.round(calculatedData.total / calculatedData.daysWorked))}</strong>/смена</span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: '8px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                {(calculatedData.totalAccepted || 0) > 0 && (
                  <span>Приёмка: <strong style={{ color: 'var(--text-secondary)' }}>{calculatedData.totalAccepted + (calculatedData.totalAcceptedLate || 0)}</strong></span>
                )}
                {(calculatedData.totalIssued || 0) > 0 && (
                  <span>Выдача: <strong style={{ color: 'var(--text-secondary)' }}>{calculatedData.totalIssued}</strong></span>
                )}
                {(calculatedData.totalReturned || 0) > 0 && (
                  <span>Возвраты: <strong style={{ color: 'var(--text-secondary)' }}>{calculatedData.totalReturned}</strong></span>
                )}
              </div>

              <button
                type="button"
                onClick={() => setShowPayModalDetails(!showPayModalDetails)}
                style={{
                  marginTop: '10px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  background: 'none', border: '1px dashed rgba(99, 102, 241, 0.3)', borderRadius: '8px', padding: '8px',
                  cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: 'var(--wb-primary)', transition: 'all 0.2s',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {showPayModalDetails
                    ? <polyline points="18 15 12 9 6 15"/>
                    : <polyline points="6 9 12 15 18 9"/>
                  }
                </svg>
                {showPayModalDetails ? 'Скрыть детализацию' : 'Показать детализацию по сменам'}
              </button>

              {showPayModalDetails && (
                <div style={{
                  marginTop: '10px', maxHeight: '350px', overflowY: 'auto',
                  borderTop: '1px solid var(--border-color)', paddingTop: '10px',
                  scrollbarWidth: 'thin',
                }}>
                  <AutoSalaryBreakdown
                    autoSalary={calculatedData}
                    filterEmployee={payModalData?.employeeId}
                    profile={profile}
                    filterPeriodStart={customPeriodStart}
                    filterPeriodEnd={customPeriodEnd}
                    setSalaryDetailModal={setSalaryDetailModal}
                    isAdmin={isAdmin}
                  />
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Режим расчёта суммы:</label>
            <div style={{ display: 'flex', background: 'var(--bg-secondary)', borderRadius: '10px', padding: '3px', border: '1px solid var(--border-color)' }}>
              <button
                type="button"
                style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: 'none', background: !isManualAmount ? 'var(--surface-color)' : 'transparent', color: !isManualAmount ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: 600, fontSize: '13px', boxShadow: !isManualAmount ? '0 2px 8px rgba(0,0,0,0.08)' : 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => setIsManualAmount(false)}
              >
                Авторасчёт
              </button>
              <button
                type="button"
                style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: 'none', background: isManualAmount ? 'var(--surface-color)' : 'transparent', color: isManualAmount ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: 600, fontSize: '13px', boxShadow: isManualAmount ? '0 2px 8px rgba(0,0,0,0.08)' : 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => setIsManualAmount(true)}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:'4px',verticalAlign:'middle'}}><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg> Вручную
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Итого к выплате:</label>
              {(() => {
                const isDataUnavailable = Boolean(payModalData?.financialDataUnavailable || calculatedData?.financialDataUnavailable || payModalData?.status === 'data_unavailable' || calculatedData?.status === 'data_unavailable')
                const isRev = Boolean(payModalData?.isReviewRequired || calculatedData?.isReviewRequired || payModalData?.hasUnresolvedLegacy || calculatedData?.hasUnresolvedLegacy)
                const hasOpenShift = Boolean(payModalData?.isOpenShift || payModalData?.hasOpenShift || calculatedData?.isOpenShift || calculatedData?.hasOpenShift)
                const isIncompleteChunk = Boolean(payModalData?.isCompleteChunk === false || calculatedData?.isCompleteChunk === false)

                if (!isManualAmount) {
                  return (
                    <div style={{
                      fontSize: (isDataUnavailable || isRev || hasOpenShift || isIncompleteChunk) ? '16px' : '24px',
                      fontWeight: 800,
                      padding: '14px',
                      borderRadius: '10px',
                      border: isDataUnavailable ? '1px solid rgba(239, 68, 68, 0.3)' : (isRev ? '1px solid rgba(245, 158, 11, 0.3)' : (hasOpenShift ? '1px solid rgba(239, 68, 68, 0.3)' : (isIncompleteChunk ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(99, 102, 241, 0.2)'))),
                      width: '100%',
                      background: isDataUnavailable ? 'rgba(239, 68, 68, 0.08)' : (isRev ? 'rgba(245, 158, 11, 0.08)' : (hasOpenShift ? 'rgba(239, 68, 68, 0.08)' : (isIncompleteChunk ? 'rgba(245, 158, 11, 0.08)' : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(139, 92, 246, 0.05))'))),
                      color: isDataUnavailable ? '#b91c1c' : (isRev ? '#b45309' : (hasOpenShift ? '#b91c1c' : (isIncompleteChunk ? '#b45309' : 'var(--wb-primary)'))),
                      textAlign: 'center',
                      letterSpacing: '0.5px',
                    }}>
                      {isDataUnavailable ? 'Данные недоступны' : (isRev ? 'Требуется сверка' : (hasOpenShift ? 'Смена не завершена' : (isIncompleteChunk ? 'Период не завершён' : (isCalculating && !calculatedData ? 'расчёт...' : formatMoney(Number(payAmount) || 0)))))}
                    </div>
                  )
                }
                return null
              })()}
              {isManualAmount && (
                <input
                  type="text"
                  inputMode="numeric"
                  className="input"
                  value={payAmount}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d]/g, '')
                    setPayAmount(v)
                  }}
                  style={{
                    fontSize: '20px', fontWeight: 700, padding: '10px 14px', borderRadius: '8px',
                    border: '1px solid var(--border-color)', width: '100%',
                    background: 'var(--surface-color)', color: 'var(--text-primary)',
                    textAlign: 'center', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
                  }}
                  placeholder="Введите сумму"
                />
              )}
            </div>

            {(() => {
              const hasOpenShift = Boolean(payModalData?.isOpenShift || payModalData?.hasOpenShift || calculatedData?.isOpenShift || calculatedData?.hasOpenShift)
              const isIncompleteChunk = Boolean(payModalData?.isCompleteChunk === false || calculatedData?.isCompleteChunk === false)
              const shiftsCountInChunk = payModalData?.shiftsCountInChunk ?? calculatedData?.shiftsCountInChunk ?? null
              const requiredShiftsCount = payModalData?.requiredShiftsCount ?? calculatedData?.requiredShiftsCount ?? null

              return (
                <>
                  {hasOpenShift && (
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.08)',
                      border: '1px solid rgba(239, 68, 68, 0.25)',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontSize: '12px',
                      color: '#b91c1c',
                      lineHeight: '1.4'
                    }}>
                      <span style={{ fontSize: '16px' }}>⏳</span>
                      <div>
                        <strong>Смена ещё не завершена.</strong> Выплата за открытую смену заблокирована до её закрытия.
                      </div>
                    </div>
                  )}

                  {isIncompleteChunk && !isManualAmount && (
                    <div style={{
                      background: 'rgba(245, 158, 11, 0.08)',
                      border: '1px solid rgba(245, 158, 11, 0.25)',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontSize: '12px',
                      color: '#b45309',
                      lineHeight: '1.4'
                    }}>
                      <span style={{ fontSize: '16px' }}>ℹ️</span>
                      <div>
                        <strong>Период не завершён</strong> (накоплено {shiftsCountInChunk || '?'} из {requiredShiftsCount || 'N'} смен). Плановая выплата заблокирована до закрытия всех смен периода.
                      </div>
                    </div>
                  )}

                  {isIncompleteChunk && isManualAmount && (
                    <div style={{
                      background: 'rgba(245, 158, 11, 0.12)',
                      border: '1px solid rgba(245, 158, 11, 0.35)',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontSize: '12px',
                      color: '#b45309',
                      lineHeight: '1.4'
                    }}>
                      <span style={{ fontSize: '16px' }}>⚠️</span>
                      <div>
                        <strong>Ручная внеплановая выплата:</strong> расчётный период {requiredShiftsCount ? `${requiredShiftsCount} смен ` : ''}ещё не завершён (накоплено {shiftsCountInChunk || '?'} из {requiredShiftsCount || 'N'} смен). Это ручное исключение владельца, а не плановая выплата по графику.
                      </div>
                    </div>
                  )}
                </>
              )
            })()}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Комментарий к выплате:</label>
              <input
                type="text"
                className="input"
                value={payComment}
                onChange={(e) => {
                  setPayComment(e.target.value)
                  setIsCommentManuallyEdited(true)
                }}
                placeholder="Например: Выплата ЗП за период"
                style={{ fontSize: '13px', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', width: '100%', background: 'var(--surface-color)', color: 'var(--text-primary)' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button
              className="btn btn-ghost"
              style={{ flex: 1 }}
              onClick={() => setPayModalData(null)}
              disabled={payingSalary}
            >
              Отмена
            </button>
            {(() => {
              const isDataUnavailable = Boolean(payModalData?.financialDataUnavailable || calculatedData?.financialDataUnavailable || payModalData?.status === 'data_unavailable' || calculatedData?.status === 'data_unavailable')
              const isUnresolvedRev = Boolean(payModalData?.isReviewRequired || calculatedData?.isReviewRequired || payModalData?.hasUnresolvedLegacy || calculatedData?.hasUnresolvedLegacy)
              const hasOpenShift = Boolean(payModalData?.isOpenShift || payModalData?.hasOpenShift || calculatedData?.isOpenShift || calculatedData?.hasOpenShift)
              const isIncompleteChunk = Boolean(payModalData?.isCompleteChunk === false || calculatedData?.isCompleteChunk === false)
              // Manual override разрешен ТОЛЬКО для incomplete chunk, но СТРОГО ЗАПРЕЩЕН при isDataUnavailable, isUnresolvedRev, hasOpenShift!
              const isRev = Boolean(isDataUnavailable || isUnresolvedRev || hasOpenShift || (!isManualAmount && isIncompleteChunk))

              return (
            <button
              className="btn btn-primary"
              style={{ flex: 1 }}
              disabled={payingSalary || !payAmount || Number(payAmount) <= 0 || isCalculating || isRev}
              onClick={async () => {
                if (isDataUnavailable) return
                const amt = Number(payAmount)
                if (isNaN(amt) || amt <= 0) {
                  toast.error('Введите корректную сумму')
                  return
                }
                setPayingSalary(true)
                try {
                  let reasonCode = 'all'
                  if (!isManualAmount) {
                    if (includeRate && !includeBonuses) reasonCode = 'rate'
                    else if (!includeRate && includeBonuses) reasonCode = 'bonus'
                  } else {
                    reasonCode = 'custom'
                  }

                  const paymentData = {
                    employee_id: payModalData.employeeId,
                    type: 'salary',
                    amount: amt,
                    period_start: customPeriodStart,
                    period_end: customPeriodEnd,
                    created_by: profile.id,
                    comment: payComment || null,
                    reason_code: reasonCode,
                    allocations: [
                      {
                        period_start: customPeriodStart,
                        period_end: customPeriodEnd,
                        allocated_amount: amt,
                        allocation_type: 'salary_period'
                      }
                    ]
                  }

                  const result = await api.createPayment(paymentData)

                  // Проверяем предупреждение о пересечении периодов
                  if (result && result.warning === 'overlap') {
                    const overlaps = result.overlapping_payments || []
                    const overlapInfo = overlaps.map(o => {
                      const start = new Date(o.period_start).toLocaleDateString('ru-RU')
                      const end = new Date(o.period_end).toLocaleDateString('ru-RU')
                      return `• ${start} — ${end}: ${Number(o.amount).toLocaleString('ru-RU')} ₽`
                    }).join('\n')

                    const confirmed = window.confirm(
                      `За этот период уже есть выплаты:\n\n${overlapInfo}\n\nВсё равно провести новую выплату?`
                    )

                    if (confirmed) {
                      // Повторный запрос с force_overlap
                      await api.createPayment({ ...paymentData, force_overlap: true })
                      toast.success('Выплата успешно проведена')
                      setRefreshTrigger(prev => prev + 1)
                      setPayModalData(null)
                    }
                    // Если не подтвердил — просто разблокируем кнопку
                    return
                  }

                  toast.success('Выплата успешно проведена')
                  setRefreshTrigger(prev => prev + 1)
                  setPayModalData(null)
                } catch (err) {
                  toast.error('Ошибка проведения выплаты: ' + err.message)
                } finally {
                  setPayingSalary(false)
                }
              }}
            >
              {isDataUnavailable
                ? 'Выплата заблокирована (данные недоступны)'
                : (isRev
                    ? (hasOpenShift
                        ? 'Выплата заблокирована (смена не завершена)'
                        : 'Выплата заблокирована (требуется сверка)')
                    : (payingSalary
                        ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{animation:'spin 1s linear infinite'}}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg> Проведение...</>
                        : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Подтвердить</>
                      )
                  )}
            </button>
              )
            })()}
          </div>
        </div>
      )}
    </Modal>
  )
}
